import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { authApi } from '../services/api/auth';
import { onboardingApi } from '../services/api/onboarding';
import { toast } from 'react-hot-toast';
import { supabase } from '../config/supabase';

export function useAuth() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUserAndToken } = useAuthContext();

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const { user, token } = await authApi.signIn(email, password);
      if (!setUserAndToken) {
        throw new Error('Authentication context not properly initialized');
      }
      await setUserAndToken(user, token);

      // Check onboarding status
      try {
        const { status } = await onboardingApi.getStatus();
        if (status !== 'completed') {
          navigate('/onboarding/markets');
        } else {
          navigate('/strategy/dashboard');
        }
      } catch (error) {
        // If we can't get onboarding status, default to onboarding
        navigate('/onboarding/markets');
      }

      toast.success('Successfully signed in!');
    } catch (err) {
      console.error('Sign in error:', err);
      toast.error(err.response?.data?.error || 'Failed to sign in');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (userData) => {
    setLoading(true);
    try {
      const { user, token } = await authApi.signUp(userData);
      if (!setUserAndToken) {
        throw new Error('Authentication context not properly initialized');
      }
      await setUserAndToken(user, token);
      toast.success('Account created successfully!');
      navigate('/onboarding/markets');
    } catch (err) {
      console.error('Sign up error:', err);
      toast.error(err.response?.data?.error || 'Failed to create account');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signUpWithGoogle = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth/callback',
        },
      });

      if (error) throw error;

      // The rest of the code will execute after the redirect
    } catch (err) {
      console.error('Google sign-up error:', err);
      toast.error('Failed to sign up with Google');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleAuthRedirect = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error retrieving session:', error);
      return;
    }

    if (session) {
      const { user } = session;
      await setUserAndToken(user, session.access_token);

      try {
        // Use the getProfile API function to check if the user already has a profile
        const profile = await authApi.getProfile();

        if (profile) {
          // If a profile exists, redirect to the dashboard
          navigate('/strategy/dashboard');
        } else {
          // If no profile exists, create a new one
          await createProfile({
            id: user.id,
            company_name: "",
            phone_number: null,
            country: "",
          });

          toast.success('Signed up with Google successfully!');
          navigate('/complete-profile'); // Redirect to profile completion page
        }
      } catch (err) {
        console.error('Error checking or creating profile:', err);
        toast.error('Failed to check or create user profile');
      }
    }
  };

  const createProfile = async (profileData) => {
    try {
      await authApi.createProfile(profileData);
      toast.success('Profile created successfully!');
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error('Failed to create profile');
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      await authApi.updateProfile(profileData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      await authApi.logout();
      await setUserAndToken(null, null);
      toast.success('Successfully logged out');
      navigate('/signin');
    } catch (err) {
      console.error('Logout error:', err);
      toast.error(err.response?.data?.error || 'Failed to logout');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    signIn,
    signUp,
    signUpWithGoogle,
    handleAuthRedirect,
    createProfile,
    updateProfile,
    signOut,
    loading
  };
} 